import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, ClipboardList, Menu, Pill, MessageSquareWarning, AlertTriangle, Baby, MoreHorizontal, CalendarDays, ChevronDown, Check, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useActiveRole } from "@/hooks/useActiveRole";
import { useFeatureVisibility } from "@/hooks/useFeatureVisibility";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";

const allTabs = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Today", roles: ["patient", "carer", "admin", "parent"], feature: null as null | "medications" | "allergies" | "children" | "diary" },
  { to: "/patients", icon: Users, label: "People", roles: ["carer", "admin"], feature: null },
  { to: "/enter-medications", icon: Pill, label: "Add Meds", roles: ["patient", "carer", "admin"], feature: "medications" as const },
  { to: "/enter-allergies", icon: AlertTriangle, label: "Allergies", roles: ["patient", "carer", "admin"], feature: "allergies" as const },
  { to: "/children", icon: Baby, label: "Children", roles: ["patient", "carer", "parent", "admin"], feature: "children" as const },
  { to: "/appointments", icon: CalendarDays, label: "Appointments", roles: ["patient", "carer", "parent"], feature: "diary" as const },
  { to: "/history", icon: ClipboardList, label: "Past Meds", roles: ["patient", "carer", "admin"], feature: "medications" as const },
  { to: "/alert-admin", icon: MessageSquareWarning, label: "Alert", roles: ["patient", "carer", "parent"], feature: null },
  { to: "/admin", icon: ShieldCheck, label: "Admin", roles: ["admin"], feature: null },
  { to: "/settings", icon: Menu, label: "Settings", roles: ["patient", "carer", "admin", "parent"], feature: null },
];

const roleLabels: Record<string, string> = {
  patient: "Person",
  carer: "Carer",
  admin: "Admin",
  parent: "Parent",
};

const VISIBLE_LIMIT = 4;

export function BottomTabs() {
  const location = useLocation();
  const { activeRole, availableRoles, setActiveRole } = useActiveRole();
  const { showMedications, showAllergies, showChildren, showDiary } = useFeatureVisibility();
  const [moreOpen, setMoreOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const canSwitchRole = availableRoles.length > 1;

  const visibleTabs = allTabs.filter((tab) => {
    if (!tab.roles.includes(activeRole)) return false;
    if (tab.feature === "medications" && !showMedications) return false;
    if (tab.feature === "allergies" && !showAllergies) return false;
    if (tab.feature === "children" && !showChildren) return false;
    if (tab.feature === "diary" && !showDiary) return false;
    return true;
  });
  const needsOverflow = visibleTabs.length > VISIBLE_LIMIT;
  const primary = needsOverflow ? visibleTabs.slice(0, VISIBLE_LIMIT - 1) : visibleTabs;
  const overflow = needsOverflow ? visibleTabs.slice(VISIBLE_LIMIT - 1) : [];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-card safe-bottom" aria-label="Main navigation">
      <div className="flex justify-center pt-2 pb-1">
        {canSwitchRole ? (
          <Sheet open={roleOpen} onOpenChange={setRoleOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary bg-primary/15 px-6 py-2 text-base font-semibold capitalize text-primary hover:bg-primary/25 active:scale-95 transition min-h-[44px] min-w-[160px] shadow-sm"
                aria-label={`Current role: ${roleLabels[activeRole] || activeRole}. Tap to switch role.`}
              >
                <span>{roleLabels[activeRole] || activeRole}</span>
                <ChevronDown className="h-5 w-5" aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-xl">
              <SheetHeader className="text-left mb-2">
                <SheetTitle>Switch role</SheetTitle>
              </SheetHeader>
              <p className="text-sm text-muted-foreground mb-3">
                You have access to multiple roles. Pick which one to use right now.
              </p>
              <div className="flex flex-col gap-2 pb-6">
                {availableRoles.map((role) => {
                  const isActive = role === activeRole;
                  return (
                    <button
                      key={role}
                      onClick={() => {
                        setActiveRole(role);
                        setRoleOpen(false);
                      }}
                      className={cn(
                        "flex items-center justify-between gap-3 px-4 py-3 rounded-lg border text-left transition-colors min-h-[56px]",
                        isActive ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                      )}
                    >
                      <span className="font-medium capitalize">{roleLabels[role] || role}</span>
                      {isActive && <Check className="h-5 w-5 text-primary" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          <Badge variant="outline" className="text-[10px] h-4 capitalize px-1.5">
            {roleLabels[activeRole] || activeRole}
          </Badge>
        )}
      </div>
      <div className="flex items-center justify-around h-16">
        {primary.map((tab) => {
          const isActive = location.pathname.startsWith(tab.to);
          return (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 min-w-[48px] min-h-[48px] px-2 py-1 text-xs transition-colors rounded-md",
                isActive ? "text-primary" : "text-muted-foreground"
              )}
            >
              <tab.icon className="h-6 w-6" aria-hidden="true" />
              <span className="font-medium leading-tight">{tab.label}</span>
            </Link>
          );
        })}
        {needsOverflow && (
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                className={cn(
                  "flex flex-col items-center justify-center gap-0.5 min-w-[48px] min-h-[48px] px-2 py-1 text-xs transition-colors rounded-md",
                  overflow.some((t) => location.pathname.startsWith(t.to)) ? "text-primary" : "text-muted-foreground"
                )}
                aria-label="More navigation"
              >
                <MoreHorizontal className="h-6 w-6" aria-hidden="true" />
                <span className="font-medium leading-tight">More</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-xl">
              <SheetHeader className="text-left mb-4"><SheetTitle>More</SheetTitle></SheetHeader>
              <div className="grid grid-cols-3 gap-3 pb-6">
                {overflow.map((tab) => (
                  <Link
                    key={tab.to}
                    to={tab.to}
                    onClick={() => setMoreOpen(false)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <tab.icon className="h-6 w-6 text-primary" aria-hidden="true" />
                    <span className="text-xs font-medium text-center">{tab.label}</span>
                  </Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </nav>
  );
}
